import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { guestGuard } from './core/guards/guest.guard';
import { HomeComponent } from './features/home/home.component';
import { PlaceholderComponent } from './shared/components/placeholder/placeholder.component';

export const routes: Routes = [
  {
    path: '',
    component: HomeComponent,
    data: {
      title: 'Galopa — Secondhand Saddle Marketplace',
      description:
        'Trusted sellers, secure transactions, confident riders. Buy and sell secondhand saddles with buyer-protected payments and free resells at 0% commission.',
      lcpImage: 'assets/hero/saddle-close.webp',
    },
  },
  {
    path: 'sell',
    loadComponent: () => import('./features/sell/sell.component').then((m) => m.SellComponent),
    data: {
      title: 'Sell Your Saddle | Galopa',
      description:
        'List your secondhand saddle for free on Galopa and reach verified buyers, with secure, protected transactions.',
      lcpImage: 'assets/marketing/sell-hero.webp',
    },
  },
  {
    path: 'buy',
    loadComponent: () => import('./features/buy/buy.component').then((m) => m.BuyComponent),
    data: {
      title: 'Buy a Saddle | Galopa',
      description:
        'Browse secondhand saddles from verified sellers, backed by secure, protected payments and free resells at 0% commission.',
      lcpImage: 'assets/marketing/buy-hero.webp',
    },
  },
  {
    path: 'want-a-saddle',
    loadComponent: () =>
      import('./features/want-a-saddle/want-a-saddle.component').then((m) => m.WantASaddleComponent),
    data: {
      title: 'Wanted Saddle | Galopa',
      description:
        'Post what saddle you’re looking for and let sellers with a match respond directly — free to post, no browsing required.',
      lcpImage: 'assets/marketing/wanted-hero.webp',
    },
  },
  {
    path: 'saddle-finder',
    loadComponent: () =>
      import('./features/saddle-finder/saddle-finder.component').then(
        (m) => m.SaddleFinderComponent,
      ),
    data: {
      title: 'Find a Saddle | Galopa',
      description:
        "Tell us what you're looking for and we'll help you find it — on Galopa and across the Australian second-hand saddle market. Free, no account needed.",
      lcpImage: 'assets/marketing/wanted-hero.webp',
    },
  },
  {
    path: 'secure-trade',
    loadComponent: () =>
      import('./features/secure-trade/secure-trade-landing/secure-trade-landing.component').then(
        (m) => m.SecureTradeLandingComponent,
      ),
    data: {
      title: 'Secure Trade | Galopa',
      description:
        'Struck a saddle deal off-platform? Galopa holds the payment safely until it arrives as described — Australia-wide, verified sellers, no leap of faith.',
      lcpImage: 'assets/marketing/secure-trade-hero.webp',
    },
  },
  {
    path: 'shipping-calculator',
    loadComponent: () =>
      import('./features/shipping-calculator/shipping-calculator.component').then(
        (m) => m.ShippingCalculatorComponent,
      ),
    data: {
      title: 'Saddle Shipping Cost Calculator | Galopa',
      description:
        'Estimate the cost to post a saddle anywhere in Australia — a live, approximate Australia Post quote based on saddle-sized box profiles.',
      lcpImage: 'assets/hero/saddle-close.webp',
    },
  },
  {
    path: 'secure-trade/invite',
    loadComponent: () =>
      import('./features/secure-trade/secure-trade-invite/secure-trade-invite.component').then(
        (m) => m.SecureTradeInviteComponent,
      ),
    data: { title: 'Trade Invite | Galopa' },
  },
  {
    path: 'login',
    loadComponent: () =>
      import('./features/auth/login/login.component').then((m) => m.LoginComponent),
    canActivate: [guestGuard],
    data: { title: 'Log In | Galopa' },
  },
  {
    path: 'signup',
    loadComponent: () =>
      import('./features/auth/signup/signup.component').then((m) => m.SignupComponent),
    canActivate: [guestGuard],
    data: { title: 'Sign Up | Galopa' },
  },
  {
    path: 'forgot-password',
    loadComponent: () =>
      import('./features/auth/forgot-password/forgot-password.component').then(
        (m) => m.ForgotPasswordComponent,
      ),
    canActivate: [guestGuard],
    data: { title: 'Reset Your Password | Galopa' },
  },
  {
    path: 'reset-password',
    loadComponent: () =>
      import('./features/auth/reset-password/reset-password.component').then(
        (m) => m.ResetPasswordComponent,
      ),
    canActivate: [guestGuard],
    data: { title: 'Reset Your Password | Galopa' },
  },
  // No guestGuard: reachable both signed-in right after signup (auto-login
  // leaves the account unverified) and signed-out after a blocked login
  // attempt.
  {
    path: 'verify-email',
    loadComponent: () =>
      import('./features/auth/verify-email/verify-email.component').then(
        (m) => m.VerifyEmailComponent,
      ),
    data: { title: 'Verify Your Email | Galopa' },
  },
  {
    path: 'join/:code',
    loadComponent: () => import('./features/join/join.component').then((m) => m.JoinComponent),
    data: {
      title: 'Join Galopa',
      description: 'Sign up to Galopa with an exclusive invite.',
    },
  },
  {
    path: 'shop',
    loadComponent: () => import('./features/shop/shop.component').then((m) => m.ShopComponent),
    data: {
      title: 'Shop Saddles | Galopa',
      description:
        'Browse verified secondhand saddles across dressage, jump, western, endurance and more — every purchase backed by buyer-protected payments.',
      lcpImage: 'dynamic',
    },
  },
  {
    path: 'product/:id',
    loadComponent: () =>
      import('./features/product-detail/product-detail.component').then(
        (m) => m.ProductDetailComponent,
      ),
    // Fallback only — ProductDetailComponent overrides via SeoService once
    // the listing loads, with the actual title/description/price.
    data: {
      title: 'Saddle Listing | Galopa',
      description:
        'Verified secondhand saddle listing on Galopa, backed by buyer-protected payments.',
      lcpImage: 'dynamic',
    },
  },
  {
    path: 'wanted',
    loadComponent: () =>
      import('./features/wanted-ads/wanted-ads.component').then((m) => m.WantedAdsComponent),
    data: {
      title: 'Wanted Saddle | Galopa',
      description:
        'Browse saddles buyers are actively looking for and respond with one of your own listings.',
    },
  },
  {
    path: 'wanted/:id',
    loadComponent: () =>
      import('./features/wanted-ad-detail/wanted-ad-detail.component').then(
        (m) => m.WantedAdDetailComponent,
      ),
    data: {
      title: 'Wanted Saddle | Galopa',
      description: 'A saddle a Galopa buyer is looking for.',
    },
  },
  {
    path: 'terms',
    loadComponent: () =>
      import('./features/legal/terms/terms.component').then((m) => m.TermsComponent),
    data: { title: 'Terms of Service | Galopa' },
  },
  {
    path: 'privacy',
    loadComponent: () =>
      import('./features/legal/privacy/privacy.component').then((m) => m.PrivacyComponent),
    data: { title: 'Privacy Policy | Galopa' },
  },
  {
    path: 'help',
    loadComponent: () => import('./features/help/help.component').then((m) => m.HelpComponent),
    data: {
      title: 'Help & FAQs | Galopa',
      description: 'Answers to common questions about buying and selling saddles on Galopa.',
    },
  },
  {
    path: 'contact',
    loadComponent: () =>
      import('./features/contact/contact.component').then((m) => m.ContactComponent),
    data: {
      title: 'Contact Us | Galopa',
      description: 'Get in touch with the Galopa team about buying, selling, or your account.',
    },
  },
  {
    path: 'account',
    loadComponent: () =>
      import('./features/account/account-layout/account-layout.component').then(
        (m) => m.AccountLayoutComponent,
      ),
    canActivate: [authGuard],
    children: [
      { path: '', redirectTo: 'overview', pathMatch: 'full' },
      {
        path: 'overview',
        loadComponent: () =>
          import('./features/account/account-overview/account-overview.component').then(
            (m) => m.AccountOverviewComponent,
          ),
        data: { title: 'My Account | Galopa' },
      },
      {
        path: 'orders',
        loadComponent: () =>
          import('./features/account/account-orders/account-orders.component').then(
            (m) => m.AccountOrdersComponent,
          ),
        data: { title: 'My Orders | Galopa' },
      },
      {
        path: 'secure-trade/new',
        loadComponent: () =>
          import('./features/account/account-secure-trade-form/account-secure-trade-form.component').then(
            (m) => m.AccountSecureTradeFormComponent,
          ),
        data: { title: 'Start a Secure Trade | Galopa' },
      },
      {
        path: 'secure-trade/:id',
        loadComponent: () =>
          import(
            './features/account/account-secure-trade-detail/account-secure-trade-detail.component'
          ).then((m) => m.AccountSecureTradeDetailComponent),
        data: { title: 'Secure Trade | Galopa' },
      },
      {
        path: 'secure-trade',
        loadComponent: () =>
          import('./features/account/account-secure-trade/account-secure-trade.component').then(
            (m) => m.AccountSecureTradeComponent,
          ),
        data: { title: 'My Secure Trades | Galopa' },
      },
      {
        path: 'selling',
        loadComponent: () =>
          import('./features/account/account-selling/account-selling.component').then(
            (m) => m.AccountSellingComponent,
          ),
        data: { title: 'Selling | Galopa' },
      },
      {
        path: 'selling/:id',
        loadComponent: () =>
          import('./features/account/account-selling-detail/account-selling-detail.component').then(
            (m) => m.AccountSellingDetailComponent,
          ),
        data: { title: 'Sale Details | Galopa' },
      },
      {
        path: 'listings/new',
        loadComponent: () =>
          import('./features/account/account-listing-form/account-listing-form.component').then(
            (m) => m.AccountListingFormComponent,
          ),
        data: { title: 'New Listing | Galopa' },
      },
      {
        path: 'listings/:id/edit',
        loadComponent: () =>
          import('./features/account/account-listing-form/account-listing-form.component').then(
            (m) => m.AccountListingFormComponent,
          ),
        data: { title: 'Edit Listing | Galopa' },
      },
      {
        path: 'listings',
        loadComponent: () =>
          import('./features/account/account-listings/account-listings.component').then(
            (m) => m.AccountListingsComponent,
          ),
        data: { title: 'My Listings | Galopa' },
      },
      {
        path: 'wanted-ads/new',
        loadComponent: () =>
          import('./features/account/account-wanted-ad-form/account-wanted-ad-form.component').then(
            (m) => m.AccountWantedAdFormComponent,
          ),
        data: { title: 'New Wanted Saddle | Galopa' },
      },
      {
        path: 'wanted-ads/:id/edit',
        loadComponent: () =>
          import('./features/account/account-wanted-ad-form/account-wanted-ad-form.component').then(
            (m) => m.AccountWantedAdFormComponent,
          ),
        data: { title: 'Edit Wanted Saddle | Galopa' },
      },
      {
        path: 'wanted-ads',
        loadComponent: () =>
          import('./features/account/account-wanted-ads/account-wanted-ads.component').then(
            (m) => m.AccountWantedAdsComponent,
          ),
        data: { title: 'My Wanted Saddles | Galopa' },
      },
      {
        path: 'saved',
        loadComponent: () =>
          import('./features/account/account-saved/account-saved.component').then(
            (m) => m.AccountSavedComponent,
          ),
        data: { title: 'Saved Saddles | Galopa' },
      },
      {
        path: 'messages',
        loadComponent: () =>
          import('./features/account/account-messages/account-messages.component').then(
            (m) => m.AccountMessagesComponent,
          ),
        data: { title: 'Messages | Galopa' },
      },
      {
        path: 'profile',
        loadComponent: () =>
          import('./features/account/account-profile/account-profile.component').then(
            (m) => m.AccountProfileComponent,
          ),
        data: { title: 'My Profile | Galopa' },
      },
      {
        path: 'refer',
        loadComponent: () =>
          import('./features/account/account-refer/account-refer.component').then(
            (m) => m.AccountReferComponent,
          ),
        data: { title: 'Refer a Friend | Galopa' },
      },
    ],
  },
  {
    path: '**',
    component: PlaceholderComponent,
    data: { title: 'Page not found', description: "We couldn't find what you were looking for." },
  },
];
